import { createFileRoute } from "@tanstack/react-router";
import {
  Button,
  Chip,
  FieldError,
  Input,
  Label,
  Skeleton,
  Table,
  TextField,
} from "@heroui/react";
import { ReactNode, useState } from "react";

import {
  OrderStatus,
  useAccountedOrder,
  useOrderByBarcode,
  useOrderInvoiceDocumentByPackageId,
} from "@/features/order/use-orders.ts";
import { orderTools } from "@/shared/helper/order.ts";
import { PageLayout } from "@/shared/components/page-layout.ui.tsx";
import { dt } from "@/shared/helper/datetime";

export const Route = createFileRoute("/dashboard/courier_orders_accept")({
  component: RouteComponent,
});

const statusColorMap: Record<
  OrderStatus,
  "success" | "danger" | "warning" | "accent" | "default"
> = {
  created: "default",
  cancelled: "danger",
  accepted: "accent",
  going_to_sender: "accent",
  decided: "danger",
  submitted: "warning",
  accounted: "warning",
  checked: "default",
  revision: "danger",
  dispatched: "default",
  transit: "accent",
  arrived: "accent",
  assigned: "accent",
  delivers_recipient: "accent",
  completed: "success",
};

const statusBorderMap: Record<OrderStatus, string> = {
  created: "border-t-neutral-400",
  cancelled: "border-t-red-400",
  accepted: "border-t-blue-400",
  going_to_sender: "border-t-blue-400",
  decided: "border-t-red-400",
  submitted: "border-t-amber-400",
  accounted: "border-t-amber-400",
  checked: "border-t-purple-400",
  revision: "border-t-red-400",
  dispatched: "border-t-purple-400",
  transit: "border-t-blue-400",
  arrived: "border-t-blue-400",
  assigned: "border-t-blue-400",
  delivers_recipient: "border-t-blue-400",
  completed: "border-t-green-500",
};

const Section = ({ title }: { title: string }) => (
  <div className="col-span-3 font-semibold text-base text-neutral-700 border-b border-neutral-200 pb-2 mt-6">
    {title}
  </div>
);

const InfoCard = ({
  label,
  value,
}: {
  label: string;
  value: string | number | ReactNode;
}) => (
  <div className="flex flex-col gap-1 bg-neutral-50 rounded-xl px-4 py-3 border border-neutral-100">
    <div className="text-xs text-neutral-400 font-medium uppercase tracking-wide">
      {label}
    </div>
    <div className="text-sm font-semibold text-neutral-800">{value}</div>
  </div>
);

function OrderSkeleton() {
  return (
    <div className="max-w-[960px] mx-auto w-full flex flex-col gap-4">
      <Skeleton className="h-20 w-full rounded-2xl" />
      <div className="grid grid-cols-3 gap-3">
        {Array.from({ length: 9 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

function RouteComponent() {
  const [barcode, setBarcode] = useState<string>("");

  const { data, refetch, isFetching } = useOrderByBarcode({
    enabled: barcode.length > 10,
    barcode,
  });

  const { mutateAsync: mutateAccounted, isPending: isPendingAccounted } =
    useAccountedOrder({
      onSuccess: () => {
        refetch();
      },
    });

  const doc = useOrderInvoiceDocumentByPackageId();
  const [printingPackageId, setPrintingPackageId] = useState<number | null>(
    null,
  );

  const order = data?.data;
  const isSearching = barcode.length > 10 && isFetching;

  return (
    <PageLayout title="Принят заказ у курьера">
      <div className="flex flex-col pt-4">
        {/* Search zone */}
        <div className="flex justify-center py-10">
          <div className="flex flex-col items-center gap-4 bg-neutral-50 border border-neutral-200 rounded-2xl px-8 py-6 w-full max-w-md shadow-sm">
            <svg
              className="text-neutral-300"
              fill="none"
              height="48"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
              width="48"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M3 9V6a1 1 0 0 1 1-1h2M3 15v3a1 1 0 0 0 1 1h2M21 9V6a1 1 0 0 0-1-1h-2M21 15v3a1 1 0 0 1-1 1h-2" />
              <line x1="7" x2="7" y1="8" y2="16" />
              <line x1="10" x2="10" y1="8" y2="16" />
              <line x1="13" x2="13" y1="8" y2="16" />
              <line x1="16" x2="16" y1="8" y2="16" />
            </svg>
            <TextField className="w-full">
              <Label>Штрих-код</Label>
              <Input
                className="w-full"
                type="text"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
              />
              <FieldError />
              <p className="text-xs text-neutral-500 mt-1">
                Ввести вручную или просканировать штрихкод
              </p>
            </TextField>
            {!order && barcode.length <= 10 && (
              <p className="text-sm text-neutral-400 text-center">
                Отсканируйте штрихкод или введите номер заказа
              </p>
            )}
          </div>
        </div>

        {isSearching && <OrderSkeleton />}

        {order && !isSearching && (
          <div className="max-w-[960px] mx-auto w-full flex flex-col gap-6 pb-24">
            {/* Status header card */}
            <div
              className={`border-t-4 ${statusBorderMap[order.status]} rounded-2xl border border-neutral-200 bg-white shadow-sm p-5`}
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xl font-bold text-neutral-800">
                    {order.orderTitle}
                  </span>
                  <Chip
                    color={statusColorMap[order.status] ?? "default"}
                    size="md"
                    variant="primary"
                  >
                    {orderTools.status.toString(order.status)}
                  </Chip>
                  {order.canCancel && (
                    <Chip color="danger" size="sm" variant="secondary">
                      Можно отменить
                    </Chip>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    isDisabled={order.status !== "submitted"}
                    isPending={isPendingAccounted}
                    size="md"
                    variant="primary"
                    onPress={() => mutateAccounted({ barcode })}
                  >
                    Принять посылку
                  </Button>
                </div>
              </div>
            </div>

            {/* Main info grid */}
            <div className="grid grid-cols-3 gap-3">
              <Section title="Детали заказа" />

              <InfoCard label="Номер заказа" value={order.orderTitle} />
              <InfoCard
                label="Слот доставки"
                value={dt(order.pickupSlotStart).format(
                  "DD MMMM YYYY, HH:mm",
                )}
              />
              <InfoCard label="Вес (кг)" value={order.weight || "—"} />
              <InfoCard
                label="Оплата"
                value={
                  <Chip
                    color={order.isPay ? "success" : "warning"}
                    size="sm"
                    variant="soft"
                  >
                    {order.isPay ? "Оплачен" : "Не оплачен"}
                  </Chip>
                }
              />
              <InfoCard
                label="Сумма оплаты"
                value={order.payAmount ? `${order.payAmount} ₸` : "—"}
              />
              <InfoCard
                label="Оплата получателем"
                value={
                  <Chip
                    color={order.isPayReceiver ? "warning" : "default"}
                    size="sm"
                    variant="soft"
                  >
                    {order.isPayReceiver ? "Да" : "Нет"}
                  </Chip>
                }
              />

              <Section title="Отправитель" />

              <InfoCard label="Имя" value={order.fromName} />
              <InfoCard label="Телефон" value={order.fromPhone} />
              <InfoCard
                label="Откуда"
                value={`${order.fromCityName}, ${order.fromAddress}`}
              />

              <Section title="Получатель" />

              <InfoCard label="Имя" value={order.toName} />
              <InfoCard label="Телефон" value={order.toPhone} />
              <InfoCard
                label="Куда"
                value={`${order.toCityName}, ${order.toAddress}`}
              />

              {order.packages.length > 0 && (
                <>
                  <Section title="Посылки" />
                  <div className="col-span-3">
                    <Table.Root className="rounded-xl border border-neutral-100">
                      <Table.Content aria-label="Посылки">
                        <Table.Header>
                          <Table.Column>Название</Table.Column>
                          <Table.Column>Статус</Table.Column>
                          <Table.Column>Вес (кг)</Table.Column>
                          <Table.Column>Объём. вес</Table.Column>
                          <Table.Column>Размер (д×ш×в)</Table.Column>
                          <Table.Column>Хрупкий</Table.Column>
                          <Table.Column>Дефект</Table.Column>
                          <Table.Column>Цена</Table.Column>
                          <Table.Column>Накладная</Table.Column>
                        </Table.Header>
                        <Table.Body items={order.packages}>
                          {(pkg) => (
                            <Table.Row id={String(pkg.id)}>
                              <Table.Cell>{pkg.name}</Table.Cell>
                              <Table.Cell>
                                <Chip
                                  color={statusColorMap[pkg.status] ?? "default"}
                                  size="sm"
                                  variant="soft"
                                >
                                  {orderTools.status.toString(pkg.status)}
                                </Chip>
                              </Table.Cell>
                              <Table.Cell>{pkg.weight}</Table.Cell>
                              <Table.Cell>{pkg.volumetricWeight || "—"}</Table.Cell>
                              <Table.Cell>
                                {pkg.length}×{pkg.width}×{pkg.height} см
                              </Table.Cell>
                              <Table.Cell>
                                <Chip
                                  color={pkg.isFragile ? "warning" : "default"}
                                  size="sm"
                                  variant="soft"
                                >
                                  {pkg.isFragile ? "Да" : "Нет"}
                                </Chip>
                              </Table.Cell>
                              <Table.Cell>
                                <Chip
                                  color={pkg.isDefect ? "danger" : "default"}
                                  size="sm"
                                  variant="soft"
                                >
                                  {pkg.isDefect ? "Да" : "Нет"}
                                </Chip>
                              </Table.Cell>
                              <Table.Cell>
                                {pkg.sellingPrice
                                  ? `${pkg.sellingPrice.toLocaleString("ru-RU")} ₸`
                                  : "—"}
                              </Table.Cell>
                              <Table.Cell>
                                <Button
                                  isPending={
                                    printingPackageId === pkg.id && doc.isPending
                                  }
                                  size="sm"
                                  variant="outline"
                                  onPress={() => {
                                    setPrintingPackageId(pkg.id);
                                    doc.mutateAsync({ packageId: pkg.id });
                                  }}
                                >
                                  Печать
                                </Button>
                              </Table.Cell>
                            </Table.Row>
                          )}
                        </Table.Body>
                      </Table.Content>
                    </Table.Root>
                  </div>
                </>
              )}

              {order.histories.length > 0 && (
                <>
                  <Section title="История статусов" />
                  <div className="col-span-3">
                    <Table.Root className="rounded-xl border border-neutral-100">
                      <Table.Content aria-label="История статусов">
                        <Table.Header>
                          <Table.Column>#</Table.Column>
                          <Table.Column>Статус</Table.Column>
                          <Table.Column>Дата</Table.Column>
                        </Table.Header>
                        <Table.Body
                          items={order.histories.map((status, idx) => ({
                            idx,
                            status,
                          }))}
                        >
                          {({ idx, status }) => (
                            <Table.Row id={String(idx)}>
                              <Table.Cell>{idx + 1}</Table.Cell>
                              <Table.Cell>
                                <Chip
                                  color={statusColorMap[status] ?? "default"}
                                  size="sm"
                                  variant={
                                    idx === order.histories.length - 1
                                      ? "primary"
                                      : "soft"
                                  }
                                >
                                  {orderTools.status.toString(status)}
                                </Chip>
                              </Table.Cell>
                              <Table.Cell className="text-neutral-400">
                                —
                              </Table.Cell>
                            </Table.Row>
                          )}
                        </Table.Body>
                      </Table.Content>
                    </Table.Root>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
